import { Home } from '@systemeio/digital-store-shared'
import { FileSourceEnum } from '@systemeio/file-manager'
import { Checkbox, FormInput, FormSelect, Hint, useWindowSize } from '@systemeio/ui'
import { SettingsSection } from 'modules/profile/components/settings-section'
import { AvailablePlansEnum } from 'modules/profile/user-plan/enums/user-plan-type-enum'
import { useEffect, useState } from 'react'
import CodeEditor from 'shared/components/code-editor'
import { DisclosureBox } from 'shared/components/disclosure-box'
import { EditableItemType } from 'shared/components/edit-modal'
import { ImageUploaderWithPreviewImage } from 'shared/components/file-uploader/image-uploader-with-preview-image'
import FormUrlPathInput from 'shared/components/form-url-path-input'
import { FieldLabel } from 'shared/components/form/field-label'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { usePaymentMethodsByCurrency } from 'shared/hooks/use-payment-methods-by-currency'
import useUser from 'shared/hooks/use-user'
import useUserDomains from 'shared/hooks/use-user-domains'
import { getLocaleSelectOptions } from 'shared/utils/locale/get-locale-select-options'
import { generatePathFromName } from 'shared/utils/path-utils'
import { storeDefaultErrors } from '../../constants/store-default-values'
import { StoreErrorsInterface, StoreInterface } from '../../types/stores-interface'
import { allowedDSPaymentMethods } from '../content/product/components/payment-methods-edit-modal'
import { useStorePreviewContext } from '../context/store-preview-context'
import { useStoreEdit } from '../hooks/use-store-edit'
import StoreEditForm from './store-edit-form'
import { isSioHost } from '@systemeio/util'

interface StoreEditProps {
  store: StoreInterface
}

const StoreEdit = ({ store: instance }: StoreEditProps) => {
  const { t } = useLocoTranslation()
  const { width } = useWindowSize()
  const isSm = width < 640

  const { user } = useUser()
  const { userDomainsSelect, userDomains } = useUserDomains()

  const { setPreview } = useStorePreviewContext()

  useEffect(() => {
    setPreview([
      <Home
        key={instance.id}
        profile={instance.profile}
        socialLinks={instance.socialLinks}
        items={instance.items.filter(el => !el.draft)}
        displayAffiliateLink={false}
      />,
    ])
  }, [setPreview, instance])

  const [errors, setErrors] = useState<StoreErrorsInterface>(storeDefaultErrors)
  const { onSave } = useStoreEdit({
    setErrors,
  })

  const { currencyAndPaymentMethodsEditFields } = usePaymentMethodsByCurrency<StoreInterface>({
    currency: instance.currency,
    errors,
    allowedPaymentMethods: allowedDSPaymentMethods,
  })

  const nameField: EditableItemType<StoreInterface> = {
    field: 'name',
    onRender: (value, onChange) => (
      <FormInput
        required
        key="store-name-edit"
        label={t('global.name')}
        value={value}
        onChange={e => onChange(e.target.value)}
        error={errors.name}
      />
    ),
  }

  const domainField: EditableItemType<StoreInterface> = {
    field: 'domainId',
    onRender: (value, onChange) => (
      <FormSelect
        required
        withoutCloseIcon
        key="store-domain-edit"
        onChange={value => value && onChange(value)}
        value={value}
        label={t('dashboard.communities.domain_name')}
        data={userDomainsSelect}
        error={errors.domainId}
      />
    ),
  }

  const nameAndDomainFieldsSm = [nameField, domainField]
  const nameAndDomainFields = [[nameField, domainField]]

  return (
    <>
      <StoreEditForm
        onEdit={onSave}
        instance={instance}
        editable={[
          ...(isSm ? nameAndDomainFieldsSm : nameAndDomainFields),
          {
            field: 'path',
            onRender: (value, onChange, data) => (
              <FormUrlPathInput
                key="store-path-edit"
                error={errors.path}
                value={value}
                onChange={e => onChange(e.target.value)}
                onFocus={e => {
                  if (!e.target.value) {
                    onChange(generatePathFromName(data.name))
                  }
                }}
                label={t('global.url_path')}
                domainName={userDomainsSelect?.find(el => el.id === data.domainId)?.caption}
              />
            ),
          },
          {
            field: 'locale',
            onRender: (value, onChange) => (
              <FormSelect
                required
                withoutCloseIcon
                key="store-locale-edit"
                onChange={value => value && onChange(value)}
                value={value}
                label={t('global.language')}
                data={getLocaleSelectOptions(t)}
                placeholder={t('global.language')}
                error={errors.locale}
              />
            ),
          },
          ...currencyAndPaymentMethodsEditFields,
          [
            {
              field: 'displayAffiliateLink',
              shouldRender: state =>
                isSioHost(window.location.host) &&
                userDomains?.find(domain => state.domainId === domain.id)?.isWhiteLabel === false,
              onRender: (value, onChange) => (
                <div className="flex flex-col gap-1" key="store-affiliate-link-edit">
                  <FieldLabel
                    label={
                      <Hint label={t('dashboard.store.affiliate.hint')}>
                        {t('dashboard.store.affiliate.title')}
                      </Hint>
                    }
                  />
                  <Checkbox
                    value={value}
                    onChange={value => onChange(value)}
                    label={
                      <div className={'flex gap-1'}>
                        <span>{t('dashboard.store.affiliate.checkbox.label')}</span>
                        {user?.userPlanId === AvailablePlansEnum.freemium && (
                          <Hint label={t('dashboard.store.affiliate.tooltip.label')} />
                        )}
                      </div>
                    }
                    disabled={user?.userPlanId === AvailablePlansEnum.freemium}
                  />
                </div>
              ),
            },
          ],
          [
            {
              field: 'seo',
              onRender: (value, onChange) => (
                <SettingsSection headingTitle={t('dashboard.store.seo_settings.title')}>
                  <FormInput
                    key="store-seo-title-edit"
                    label={t('dashboard.store.seo.meta_title')}
                    value={value?.title ?? ''}
                    onChange={e => onChange({ ...value, title: e.target.value })}
                    error={errors?.title}
                  />
                  <FormInput
                    key="store-seo-description-edit"
                    label={t('dashboard.store.seo.meta_description')}
                    value={value?.description ?? ''}
                    onChange={e => onChange({ ...value, description: e.target.value })}
                    error={errors?.description}
                  />
                  <FormInput
                    key="store-seo-keywords-edit"
                    label={t('dashboard.store.seo.keywords')}
                    value={value?.keywords ?? ''}
                    onChange={e => onChange({ ...value, keywords: e.target.value })}
                    error={errors?.keywords}
                  />
                  <FormInput
                    key="store-seo-author-edit"
                    label={t('dashboard.store.seo.author')}
                    value={value?.author ?? ''}
                    onChange={e => onChange({ ...value, author: e.target.value })}
                    error={errors?.author}
                  />
                  <ImageUploaderWithPreviewImage
                    key="store-seo-image-edit"
                    dropZoneClassName={'mb-4'}
                    value={value.image ? { path: value.image.path } : undefined}
                    source={FileSourceEnum.DigitalStore}
                    onSelect={file => {
                      onChange({ ...value, image: file })
                    }}
                    onRemove={() => {
                      onChange({ ...value, image: null })
                    }}
                  />
                  <DisclosureBox
                    panelClassName={'p-0'}
                    wrapperClassName={'border border-text-secondary/30 rounded-lg'}
                    title={
                      <Hint label={t('dashboard.store.seo.tracking_code_popover_title')}>
                        {t('dashboard.store.seo.tracking_code')}
                      </Hint>
                    }
                  >
                    <CodeEditor
                      onChange={text => onChange({ ...value, trackingCode: text })}
                      value={value.trackingCode}
                    />
                  </DisclosureBox>
                  <Checkbox
                    key="store-seo-hide-from-search-engine-edit"
                    value={value?.hideFromSearchEngines}
                    onChange={checked => onChange({ ...value, hideFromSearchEngines: checked })}
                    label={t('dashboard.store.seo.hide_from_search_engines')}
                  />
                </SettingsSection>
              ),
            },
          ],
        ]}
      />
    </>
  )
}

export default StoreEdit
